import 'core-js/fn/array/from';
import {
	PAYLATER_MINIMUM_ELIGIBLE_AMOUNT,
	PAYPAL_CHECKOUT_NAMESPACE,
	PAYPAL_MESSAGES_NAMESPACE,
	TARGETING_CONSENT_CATEGORY,
} from './paypal-constants';
import isDomElement from 'public/js/lib/is-dom-element';

/**
 * @typedef {{
 *   amount: number,
 *   elemOrSelector: string|Element,
 *   placement: string,
 *   style?: {
 *     color?: string,
 *     height?: number,
 *     layout?: string,
 *     logo?: { position?: string },
 *     ratio?: string,
 *   },
 * }} PaypalMessageOpts
 */

/**
 * @typedef {{
 *   Messages: (options: {
 *     amount: number,
 *     placement: string,
 *     style: PaypalMessageOpts['style'],
 *   }) => PaypalMessageInstance,
 * }} PaypalMessagesSdk
 */

/**
 * @typedef {{
 *   render: (container: Element) => Promise<void>,
 * }} PaypalMessageInstance
 */

/**
 * @typedef {(message: PaypalMessageInstance, container: Element) => void} PaypalMessageCallback
 */

/**
 * @typedef {{
 *   callback: PaypalMessageCallback|undefined,
 *   deadlineMs: number,
 *   opts: PaypalMessageOpts,
 * }} PaypalMessageRequest
 */

const MESSAGE_SDK_POLL_INTERVAL_MS = 150;
const MESSAGE_SDK_INITIAL_WAIT_MS = 1_500;
const MESSAGE_SDK_AFTER_CONSENT_WAIT_MS = 30_000;

let paypalMessageConsentListenerRegistered = false;
/** @type {(() => void)|null} */
let pendingPaypalMessageRetry = null;
/** @type {Map<string|Element, PaypalMessageRequest>} */
const activePaypalMessageRequests = new Map();
/** @type {WeakMap<Element, number>} */
const paypalMessageRenderGenerations = new WeakMap();

/**
 * Advance the render generation so in-flight renders for this container are ignored.
 * @param {Element} container
 * @returns {number}
 */
export const invalidatePaypalMessageRenders = (container) => {
	const nextGeneration = (paypalMessageRenderGenerations.get(container) ?? 0) + 1;
	paypalMessageRenderGenerations.set(container, nextGeneration);
	return nextGeneration;
};

/**
 * Return the window SDK for a namespace when it exposes Messages.
 * @returns {PaypalMessagesSdk|null}
 */
const getSdkWithMessages = (namespace) => {
	const sdk = window[namespace];
	return typeof sdk?.Messages === 'function' ? sdk : null;
};

/**
 * Resolve a Messages-capable PayPal SDK (messages namespace, then checkout).
 * @returns {PaypalMessagesSdk|null}
 */
const getPaypalMessagesSdk = () => (
	getSdkWithMessages(PAYPAL_MESSAGES_NAMESPACE)
	?? getSdkWithMessages(PAYPAL_CHECKOUT_NAMESPACE)
);

/** True when a Messages-capable PayPal SDK is loaded. */
export const paypalMessagesReady = () => getPaypalMessagesSdk() !== null;

/**
 * Run the pending retry when targeting consent is allowed.
 * @param {string[]} allowedCategories
 */
const retryPaypalMessageWhenTargetingAllowed = (allowedCategories) => {
	if (!allowedCategories.includes(TARGETING_CONSENT_CATEGORY)) {
		return;
	}

	const nextRender = pendingPaypalMessageRetry;
	pendingPaypalMessageRetry = null;
	nextRender?.();
};

/**
 * Defer loading until targeting consent is allowed.
 * @param {() => void} retryRender
 */
const schedulePaypalMessageAfterConsent = (retryRender) => {
	const subscribe = globalThis.rp_app?.consent?.onAllowedConsentCategoriesChange;
	if (typeof subscribe !== 'function') {
		return;
	}

	pendingPaypalMessageRetry = retryRender;
	if (paypalMessageConsentListenerRegistered) {
		return;
	}

	paypalMessageConsentListenerRegistered = true;
	subscribe(retryPaypalMessageWhenTargetingAllowed);
};

/**
 * Render a PayPal message into each container.
 * @param {Element[]} containers
 * @param {PaypalMessageInstance} message
 * @param {PaypalMessageOpts['style']} style
 * @param {PaypalMessageCallback|undefined} callback
 */
const renderMessages = (containers, message, style, callback) => {
	for (const container of containers) {
		if (style?.height) {
			container.style.height = `${style.height}px`;
		}

		const renderGeneration = invalidatePaypalMessageRenders(container);

		void message.render(container).then(
			() => {
				if (paypalMessageRenderGenerations.get(container) !== renderGeneration) {
					return;
				}
				callback?.(message, container);
			},
			() => {
				if (paypalMessageRenderGenerations.get(container) !== renderGeneration) {
					return;
				}
				container.innerHTML = '';
			},
		);
	}
};

/**
 * Render eligible PayPal Messages into matching containers.
 * @param {PaypalMessageOpts} opts
 * @param {PaypalMessageCallback|undefined} callback
 */
const renderPaypalMessage = (opts, callback) => {
	const paypal = getPaypalMessagesSdk();
	const containers = isDomElement(opts.elemOrSelector)
		? [opts.elemOrSelector]
		: Array.from(window.document.querySelectorAll(opts.elemOrSelector));
	const canRenderMessage = paypal
		&& containers.length > 0
		&& opts.amount >= PAYLATER_MINIMUM_ELIGIBLE_AMOUNT;

	if (!canRenderMessage) {
		return;
	}

	const messageStyleBase = {
		color: 'white-no-border',
		ratio: '20x1',
	};
	const placementStyle = opts.placement === 'product'
		? {
			...messageStyleBase,
			layout: 'text',
			logo: {
				position: 'top',
			},
		}
		: {
			...messageStyleBase,
			layout: 'flex',
		};
	const style = {
		...placementStyle,
		...opts.style,
	};
	const message = paypal.Messages({
		amount: opts.amount,
		placement: opts.placement,
		style,
	});

	renderMessages(containers, message, style, callback);
};

/**
 * Resume active message loads after targeting consent is granted.
 */
const continuePaypalMessageAfterConsent = () => {
	for (const request of activePaypalMessageRequests.values()) {
		request.deadlineMs = Date.now() + MESSAGE_SDK_AFTER_CONSENT_WAIT_MS;
		loadPaypalMessageRequest(request);
	}
};

/**
 * Poll for the Messages SDK, or wait for targeting consent when the poll window ends.
 * @param {PaypalMessageRequest} request
 */
const loadPaypalMessageRequest = (request) => {
	const requestKey = request.opts.elemOrSelector;
	if (activePaypalMessageRequests.get(requestKey) !== request) {
		return;
	}

	if (paypalMessagesReady()) {
		renderPaypalMessage(request.opts, request.callback);
		activePaypalMessageRequests.delete(requestKey);
		return;
	}

	if (Date.now() < request.deadlineMs) {
		setTimeout(loadPaypalMessageRequest, MESSAGE_SDK_POLL_INTERVAL_MS, request);
		return;
	}

	// Targeting may already be allowed; the messages script can still be loading after activation.
	schedulePaypalMessageAfterConsent(continuePaypalMessageAfterConsent);
};

/**
 * Initialize PayPal Messages, polling for the SDK or deferring until consent allows it.
 * @param {PaypalMessageOpts} opts
 * @param {PaypalMessageCallback} [callback]
 */
export const initPaypalMessage = (opts, callback) => {
	const request = {
		callback,
		deadlineMs: Date.now() + MESSAGE_SDK_INITIAL_WAIT_MS,
		opts,
	};
	activePaypalMessageRequests.set(opts.elemOrSelector, request);
	loadPaypalMessageRequest(request);
};
